import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Logo from './Logo';
import { Eye, EyeOff, Server, ArrowLeft } from 'lucide-react';
import { getConfiguredApiBaseUrl, setSessionItem } from '../config/runtimeConfig';

type LoginPrefillState = {
  serverUrl?: string;
  username?: string;
  password?: string;
};

const Login: React.FC = () => {
  const [credentials, setCredentials] = useState({
    username: 'admin',
    password: 'xyz123',
    serverUrl: getConfiguredApiBaseUrl() || 'https://stream-manager-url.net', // Default server URL
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((state) => state.login);

  useEffect(() => {
    const state = location.state as LoginPrefillState | null;
    if (!state) return;
    const serverUrl = (state.serverUrl || '').trim();
    const username = (state.username || '').trim();
    const password = state.password || '';
    if (!serverUrl && !username && !password) return;
    setCredentials((prev) => ({
      ...prev,
      ...(serverUrl ? { serverUrl } : {}),
      ...(username ? { username } : {}),
      ...(password ? { password } : {}),
    }));
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const baseUrl = (credentials.serverUrl || getConfiguredApiBaseUrl() || '').trim().replace(/\/$/, '');

      // Store the server URL in sessionStorage for the API service to use
      setSessionItem('api-base-url', baseUrl);
      
      // Create Basic Auth header
      const basicAuth = btoa(`${credentials.username}:${credentials.password}`);
      
      // Make login request through same-origin /api (proxied by dev setupProxy or production Nginx).
      const axios = (await import('axios')).default;
      const response = await axios.put('/api/as/v1/auth/login', {}, {
        headers: {
          'Authorization': `Basic ${basicAuth}`,
          'accept': 'application/json',
          // Allow overriding proxy target via Server URL field (same behavior as dev proxy).
          ...(baseUrl && baseUrl !== getConfiguredApiBaseUrl().trim().replace(/\/$/, '') && {
            'x-custom-server': baseUrl
          })
        }
      });
      
      login(response.data.token);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials and server URL.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-red5-dark flex items-center justify-center p-4 relative">
      <button
        type="button"
        onClick={() => window.location.replace('/')}
        className="absolute top-4 left-4 z-10 text-white hover:text-red5-red flex items-center space-x-2 transition-colors duration-300 shrink-0"
      >
        <ArrowLeft className="h-5 w-5" />
        <span>Back to /</span>
      </button>

      <div className="max-w-md w-full space-y-8">
        <div className="bg-red5-gradient border border-red5-border rounded-lg shadow-xl p-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Logo height="40px" width="100px" />
            </div>
            <h2 className="mt-6 text-3xl font-normal text-white">
              Stream Manager
            </h2>
            <p className="mt-2 text-sm text-white opacity-80">
              Sign in to your Stream Manager
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red5-card-dark border border-red5-red text-red5-red px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="serverUrl" className="block text-sm font-medium text-white">
                  Server URL
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Server className="h-5 w-5 text-red5-red" />
                  </div>
                  <input
                    id="serverUrl"
                    name="serverUrl"
                    type="url"
                    required
                    value={credentials.serverUrl}
                    onChange={handleInputChange}
                    className="pl-10 block w-full px-3 py-2 bg-red5-card-dark border border-red5-border rounded-md shadow-sm text-white placeholder-white placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-red5-red focus:border-red5-red"
                    placeholder="https://your-sm-server.com"
                  />
                </div>
                <p className="mt-1 text-xs text-white opacity-70">
                  Enter the base URL of your Stream Manager server
                </p>
              </div>

              <div>
                <label htmlFor="username" className="block text-sm font-medium text-white">
                  Username
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={credentials.username}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 bg-red5-card-dark border border-red5-border rounded-md shadow-sm text-white placeholder-white placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-red5-red focus:border-red5-red"
                  placeholder="Enter your username"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={credentials.password}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 bg-black border border-red5-border rounded-md shadow-sm text-white placeholder-white placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-red5-red focus:border-red5-red pr-10"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-red5-red" />
                    ) : (
                      <Eye className="h-5 w-5 text-red5-red" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-red5-red text-sm font-medium rounded-md text-white bg-red5-red hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red5-red disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login; 